// BracketView — traditional tournament bracket (Phase T-UI3).
//
// Phase T-UI3 layout fix:
//   • ROW_H increased from 112 → 168 to make room for vs. + schedule strip.
//   • Card heights are bounded by ROW_H − vertical padding so cards NEVER
//     overlap, even with QF cards pulled to the midpoint of two R1 cards.
//   • Player rows are stacked with a small "vs." divider in between.
//   • New compact schedule strip below the players: Calendar + date,
//     Clock + time, MapPin + court label. Hidden chips when not set.
//
// Phase T-FIX1 polish preserved:
//   • Seed chips, emerald winner highlight + check icon, muted BYE row,
//     dashed-border TBD slots, status pills, gold Champion banner.
//
// Phase T-UI2 layout preserved:
//   • Each match in round r wrapped in slot of (2^(r-1))·ROW_H, card
//     centred. L-shape connector divs between every pair of columns.
//
// Mechanics untouched.
import React from 'react';
import {
  Trophy, Check, Clock, CalendarBlank, MapPin,
} from '@phosphor-icons/react';
import MemberLink from '../MemberLink';

// Renders a tournament entry's name(s) as clickable member link(s). A doubles
// entry shows "A / B" with each half linked separately. Falls back to plain
// text for manual entries with no linked profile.
function EntryName({ entry }) {
  if (!entry) return null;
  const { player1_profile_id, player2_profile_id, player1_name, player2_name, display_name } = entry;
  if (!player2_profile_id && !player2_name) {
    return <MemberLink profileId={player1_profile_id} name={player1_name || display_name} />;
  }
  return (
    <>
      <MemberLink profileId={player1_profile_id} name={player1_name} />
      <span className="text-[#9aa39d]"> / </span>
      <MemberLink profileId={player2_profile_id} name={player2_name} />
    </>
  );
}

const ROW_H = 192;   // height of one round-1 slot in px — fits the fixed-region
                     // admin card (header + teams + meta + result/action row).
const COL_W = 252;
const GAP_W = 48;    // gap between columns — connector lives here

const STATUS_PILL = {
  pending:   { cls: 'bg-[#f3f1ec] text-[#6a766e]',                           label: 'Pending' },
  scheduled: { cls: 'bg-amber-50 text-amber-800 ring-1 ring-amber-100',      label: 'Ready'   },
  completed: { cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100', label: 'Final'  },
};
const BYE_PILL = { cls: 'bg-[#f3f1ec] text-[#6a766e] ring-1 ring-[#e3dcd1]', label: 'Bye' };

function roundLabel(roundNumber, totalRounds) {
  if (roundNumber === totalRounds)        return 'Final';
  if (roundNumber === totalRounds - 1)    return 'Semifinals';
  if (roundNumber === totalRounds - 2)    return 'Quarterfinals';
  return `Round ${roundNumber}`;
}

function SeedChip({ seed }) {
  if (!seed) return null;
  return (
    <span
      data-testid="bracket-seed-chip"
      className="inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-[#013523] text-white text-[10px] font-semibold tracking-wide tabular-nums">
      {seed}
    </span>
  );
}

// One player slot — either a populated row (with seed + name) or a dashed
// TBD placeholder. Compact (single-line) to keep card height in check.
function PlayerRow({ entry, isWinner, isByeAdvance, slot }) {
  if (!entry) {
    return (
      <div
        data-testid={`bracket-empty-slot-${slot}`}
        className="h-7 flex items-center gap-2 px-2.5 rounded-lg border border-dashed border-[#e3dcd1] text-[#9aa39d] text-[13px] italic">
        <span className="inline-block w-[22px] h-[18px] rounded-full bg-[#f3f1ec]" aria-hidden />
        <span>TBD</span>
      </div>
    );
  }
  const winnerCls = isWinner
    ? 'bg-emerald-50 border-emerald-200 text-[#013523] font-semibold'
    : 'bg-white border-[#ece6dc] text-[#2a332c]';
  return (
    <div
      data-testid={`bracket-player-row-${slot}${isWinner ? '-winner' : ''}`}
      className={`h-7 flex items-center justify-between gap-2 px-2.5 rounded-lg border text-[13px] ${winnerCls}`}>
      <div className="flex items-center gap-2 min-w-0">
        <SeedChip seed={entry.seed} />
        <span className="truncate" title={entry.display_name}><EntryName entry={entry} /></span>
      </div>
      {isWinner && (
        <Check size={14} weight="bold" className="text-emerald-700 shrink-0" data-testid="bracket-winner-check" />
      )}
      {isByeAdvance && !isWinner && <span className="text-[9px] uppercase tracking-wider text-[#9aa39d]">Bye</span>}
    </div>
  );
}

// Compact "Date · Time · Court" strip. Hide chips when missing.
function ScheduleStrip({ scheduledAt, courtLabel, testIdPrefix }) {
  let dateChip = null;
  let timeChip = null;
  if (scheduledAt) {
    try {
      const d = new Date(scheduledAt);
      const hasTime = /T\d{2}:/.test(scheduledAt) || scheduledAt.includes(' ');
      dateChip = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      if (hasTime) {
        timeChip = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
      }
    } catch {
      // ignore
    }
  }
  return (
    <div
      data-testid={`${testIdPrefix}-schedule`}
      className="h-4 flex items-center gap-2.5 text-[10px] text-[#6a766e] overflow-hidden">
      {dateChip && (
        <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-schedule-date`}>
          <CalendarBlank size={11} weight="duotone" className="text-[#013523]" />
          {dateChip}
        </span>
      )}
      {timeChip && (
        <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-schedule-time`}>
          <Clock size={11} weight="duotone" className="text-[#013523]" />
          {timeChip}
        </span>
      )}
      {courtLabel && (
        <span className="inline-flex items-center gap-1" data-testid={`${testIdPrefix}-schedule-court`}>
          <MapPin size={11} weight="duotone" className="text-[#013523]" />
          Court {courtLabel}
        </span>
      )}
      {!dateChip && !timeChip && !courtLabel && <span className="text-[#c1bcb1]">Not scheduled</span>}
    </div>
  );
}

function MatchCard({ match: m, onMatchClick, dataTestidPrefix }) {
  const winnerId = m.winner_entry_id;
  const isBye = m.score_text === '(bye)';
  const isCompleted = m.status === 'completed';
  const clickable = !!onMatchClick && !isBye;  // admin opens dialog even on
                                               // pending matches to set
                                               // schedule / cancel
  const pill = isBye ? BYE_PILL : STATUS_PILL[m.status];

  const e1Winner = !!winnerId && m.entry1 && winnerId === m.entry1.id;
  const e2Winner = !!winnerId && m.entry2 && winnerId === m.entry2.id;
  const byeEntry = isBye ? (m.entry1 || m.entry2) : null;

  return (
    <div
      onClick={() => clickable && onMatchClick(m)}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={(e) => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onMatchClick(m); } }}
      data-testid={`${dataTestidPrefix}-match-${m.id}`}
      className={`block w-full text-left border rounded-2xl p-2.5 bg-white shadow-sm transition-all
        ${isBye ? 'border-[#ece6dc] bg-[#fbfaf8]' : 'border-[#e3dcd1]'}
        ${isCompleted && !isBye ? 'border-emerald-200' : ''}
        ${clickable ? 'hover:border-[#013523] hover:shadow-md cursor-pointer' : 'cursor-default'}`}>
      {/* TOP — match number / status */}
      <div className="h-5 flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-wider text-[#9aa39d] font-medium">
          Match {m.match_number}
        </div>
        <span
          data-testid={`${dataTestidPrefix}-match-${m.id}-status`}
          className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${pill?.cls || ''}`}>
          {pill?.label || ''}
        </span>
      </div>

      {isBye ? (
        /* BYE — advancing team + one clear note; no opponent slot, no action */
        <div className="space-y-1.5">
          <PlayerRow entry={byeEntry} isWinner slot={m.entry1 ? 'e1' : 'e2'} />
          <div
            className="h-7 flex items-center justify-center rounded-lg bg-[#f3f1ec] text-[11px] text-[#6a766e] italic"
            data-testid={`${dataTestidPrefix}-match-${m.id}-score`}>
            Bye — advances to the next round
          </div>
        </div>
      ) : (
        <>
          {/* TEAMS */}
          <PlayerRow entry={m.entry1} isWinner={e1Winner} slot="e1" />
          <div className="h-3.5 text-[9px] uppercase tracking-widest text-[#c1bcb1] text-center select-none leading-[14px]">vs</div>
          <PlayerRow entry={m.entry2} isWinner={e2Winner} slot="e2" />
          {/* META */}
          <div className="mt-1.5">
            <ScheduleStrip
              scheduledAt={m.scheduled_at}
              courtLabel={m.court_label}
              testIdPrefix={`${dataTestidPrefix}-match-${m.id}`}
            />
          </div>
          {/* RESULT + ACTION — one fixed bottom row */}
          <div className="mt-1 pt-1 border-t border-[#f0ebe1] h-8 flex items-center justify-between gap-2">
            <div
              data-testid={`${dataTestidPrefix}-match-${m.id}-score`}
              className="text-[11px] text-[#015e3f] font-semibold tabular-nums truncate">
              {m.score_text || <span className="text-[#c1bcb1] font-normal">No result</span>}
            </div>
            {clickable && m.entry1 && m.entry2 && (
              <button
                type="button"
                onClick={(ev) => { ev.stopPropagation(); onMatchClick(m); }}
                data-testid={`${dataTestidPrefix}-match-${m.id}-score-btn`}
                className={`shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.12em] transition-colors ${
                  isCompleted
                    ? 'bg-[#f5f0e6] text-[#013523] border border-[#c9a93a] hover:bg-[#fffbf0]'
                    : 'bg-[#013523] text-white hover:bg-[#025a3a]'
                }`}
              >
                {isCompleted ? 'Edit score' : 'Enter score'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// L-shape connector between consecutive columns. Computed purely from match
// geometry (no measurement).
function ConnectorColumn({ round, slotsInCol }) {
  const parents = Math.floor(slotsInCol / 2);
  const childStep = ROW_H;
  const halfChildSpan = (2 ** (round - 2));

  return (
    <div className="relative shrink-0" style={{ width: GAP_W, height: slotsInCol * childStep }} aria-hidden>
      {Array.from({ length: parents }).map((_, p) => {
        const childA = 2 * p;
        const childB = 2 * p + 1;
        const cAtop = childA * halfChildSpan * ROW_H + (halfChildSpan * ROW_H) / 2;
        const cBtop = childB * halfChildSpan * ROW_H + (halfChildSpan * ROW_H) / 2;
        const parentTop = (cAtop + cBtop) / 2;
        return (
          <React.Fragment key={p}>
            <div className="absolute bg-[#d6cfc2]" style={{ left: 0, width: GAP_W / 2, top: cAtop - 0.5, height: 1 }} />
            <div className="absolute bg-[#d6cfc2]" style={{ left: 0, width: GAP_W / 2, top: cBtop - 0.5, height: 1 }} />
            <div className="absolute bg-[#d6cfc2]" style={{ left: GAP_W / 2 - 0.5, top: cAtop, width: 1, height: cBtop - cAtop }} />
            <div className="absolute bg-[#d6cfc2]" style={{ left: GAP_W / 2, top: parentTop - 0.5, width: GAP_W / 2, height: 1 }} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function BracketView({ tournament, onMatchClick, dataTestidPrefix = 'bracket' }) {
  const matches = tournament?.matches || [];
  const totalRounds = tournament?.round_count || 0;
  if (!totalRounds) {
    return (
      <div
        className="text-sm text-[#6a766e] border border-[#e3dcd1] rounded-2xl p-6 text-center"
        data-testid={`${dataTestidPrefix}-empty`}>
        Bracket has not been generated yet.
      </div>
    );
  }
  const round1Matches = matches.filter((m) => m.round_number === 1).length;
  const slotsInCol = round1Matches || (tournament.bracket_size ? tournament.bracket_size / 2 : 0);
  const colHeight = slotsInCol * ROW_H;

  const rounds = [];
  for (let r = 1; r <= totalRounds; r += 1) {
    const rm = matches
      .filter((m) => m.round_number === r)
      .sort((a, b) => a.match_number - b.match_number);
    rounds.push({ round: r, matches: rm });
  }

  return (
    <div className="space-y-3" data-testid={dataTestidPrefix}>
      <div className="overflow-x-auto -mx-2 px-2 pb-2">
        <div className="flex min-w-max">
          {rounds.map(({ round, matches: rm }, idx) => {
            const slotHeight = (2 ** (round - 1)) * ROW_H;
            return (
              <React.Fragment key={round}>
                {idx > 0 && <ConnectorColumn round={round} slotsInCol={slotsInCol} />}
                <div
                  className="shrink-0"
                  style={{ width: COL_W }}
                  data-testid={`${dataTestidPrefix}-round-${round}`}>
                  <div className="flex items-baseline justify-between pb-1.5 mb-2 border-b border-[#ece6dc] sticky top-0 bg-[#fbfaf8]/95 backdrop-blur-sm z-10">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-[#013523] font-semibold">
                      {roundLabel(round, totalRounds)}
                    </div>
                    <div className="text-[10px] text-[#9aa39d] tabular-nums">{rm.length}</div>
                  </div>
                  <div className="relative" style={{ height: colHeight }}>
                    {rm.map((m, i) => (
                      <div
                        key={m.id}
                        className="absolute left-0 right-0 flex items-center px-1.5"
                        style={{
                          top: i * slotHeight,
                          height: slotHeight,
                        }}>
                        <div className="w-full">
                          <MatchCard match={m} onMatchClick={onMatchClick} dataTestidPrefix={dataTestidPrefix} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {tournament.champion && (
        <div
          className="mt-3 border border-[#c9a93a] rounded-2xl p-4 bg-gradient-to-br from-[#fffbf0] to-[#fff7e0]"
          data-testid={`${dataTestidPrefix}-champion`}>
          <div className="flex items-center gap-2 text-[#c9a93a]">
            <Trophy size={18} weight="fill" />
            <div className="text-xs uppercase tracking-wider font-semibold">Champion</div>
          </div>
          <div className="mt-1 text-lg text-[#013523] font-semibold"><EntryName entry={tournament.champion} /></div>
          {tournament.runner_up && (
            <div className="mt-1 text-xs text-[#6a766e]">
              Runner-up: <span className="text-[#2a332c]"><EntryName entry={tournament.runner_up} /></span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
